import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, needsIntro, needsList, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-neutral-100" data-id="element-165" data-mp="1xwgc6udciflt">
      <div className="max-w-4xl mx-auto" data-id="element-166" data-mp="21zla0jmhdt44">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-167" data-mp="220o1asnptxkk">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-neutral-900 uppercase" data-id="element-168" data-mp="1o0lmjfapfk9j">
            Still Spending Hours Preparing Heavy Equipment Training Sessions?
          </h2>
          <div className="text-lg text-neutral-800 leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-neutral-200 text-left space-y-4" data-id="element-169" data-mp="3iemjtvqlvz7i">
            <p data-id="element-170" data-mp="3nalaxr3bjvbm">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-171" data-mp="1yzdm4jp9m7lh">
                  {line}
                  <br data-id="element-172" data-mp="1u2dieffw1gxj" />
                </React.Fragment>)}
            </p>
            <p className="text-neutral-900 font-semibold" data-id="element-173" data-mp="27sqmu7tkmdi4">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-174" data-mp="23qvguq0p3by0" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-175" data-mp="1g112g40q9fbn" />}
                </React.Fragment>)}
            </p>
            <p className="text-neutral-900 font-semibold" data-id="element-176" data-mp="1b39bbc7wpx8i">{needsIntro}</p>
            <p data-id="element-177" data-mp="1b52l30dtgxkq">
              {needsList.map((line, index) => <React.Fragment key={line} data-id="element-178" data-mp="1p428y5ti98fu" data-mpi={index}>
                  {line}
                  {index < needsList.length - 1 && <br data-id="element-179" data-mp="1k1usp41n767p" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-180" data-mp="30gcq1eyvzg4g">Suddenly, you&apos;re facing the same questions again:</p>
            <ul className="space-y-2 pl-1" data-id="element-181" data-mp="2vjclx9llknfz">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-neutral-900 font-semibold" data-id="element-182" data-mp="1t5ol94c5wk0h">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-3" aria-hidden="true" data-id="element-183" data-mp="1y3gm4nusbggt" />
                  <span data-id="element-184" data-mp="226wf9a7nayor">{question}</span>
                </li>)}
            </ul>
            <p className="text-neutral-900 font-semibold" data-id="element-185" data-mp="2756crrguebdj">
              You already know how important quality heavy equipment education is.
            </p>
            <p data-id="element-186" data-mp="1acudaucyhs5r">
              But finding, organizing and preparing quality teaching materials can become a second
              job.
            </p>
            <p data-id="element-187" data-mp="1jeam4xo4hm7v">
              And when every machinery category requires a different resource, preparing your next
              lesson takes far more time than it should.
            </p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-188" data-mp="qqeif904yu1l">
          <h3 className="text-xl font-bold text-neutral-900 mb-8 text-center uppercase tracking-wide" data-id="element-189" data-mp="vpremkcwddgu">
            The Real Cost of Training Without a Complete System
          </h3>
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm shadow-neutral-900/5" data-id="element-190" data-mp="o3vz8vx5f7bo">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-neutral-800 text-sm leading-relaxed border-t first:border-t-0 border-neutral-200" data-id="element-191" data-mp="e6hhmszhochs" data-mpi={index}>
                <X size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" data-id="element-192" data-mp="3monfpjvo1wbd" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-neutral-200" data-id="element-193" data-mp="3dn7ea1mtx4ja">
            <p className="text-2xl font-bold tracking-tight text-neutral-900" data-id="element-194" data-mp="3dpaetfllyg29">
              You don’t need more disconnected machinery information.
            </p>
            <p className="text-xl mt-4 text-neutral-800 font-semibold uppercase" data-id="element-195" data-mp="1lkcus472noqr">
              You need a complete <span className="text-amber-600" data-id="element-196" data-mp="17tghdklpve4a">training system</span> that shows
              you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-197" data-mp="1crhyhzzl13d4">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-amber-600" data-id="element-198" data-mp="xuijvs6bca95">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}